import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useImageVariables } from '@/hooks/useImageVariables';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ImageIcon, Wand2, Upload, Image } from 'lucide-react';

interface ImageSelectorProps {
  selectedImage: string;
  onImageSelect: (imageUrl: string) => void;
  blogTags: string[];
  blogCategories: string[];
}

const ImageSelector: React.FC<ImageSelectorProps> = ({
  selectedImage,
  onImageSelect,
  blogTags,
  blogCategories
}) => {
  const [isAutoMode, setIsAutoMode] = useState(true);
  const [manualMode, setManualMode] = useState<'select' | 'upload' | 'generate'>('select');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const { data: imageVariables, refetch } = useImageVariables();

  // Auto-select image based on tags when auto mode is enabled
  useEffect(() => {
    if (isAutoMode && imageVariables && (blogTags.length > 0 || blogCategories.length > 0)) {
      findMatchingImage();
    }
  }, [isAutoMode, imageVariables, blogTags, blogCategories]);

  const findMatchingImage = () => {
    if (!imageVariables) return;

    const allSearchTerms = [...blogTags, ...blogCategories];
    
    // Find images with matching keywords
    const matchingImages = imageVariables.filter(variable => 
      variable.image_url && 
      variable.keywords.some(keyword => 
        allSearchTerms.some(term => 
          keyword.toLowerCase().includes(term.toLowerCase()) ||
          term.toLowerCase().includes(keyword.toLowerCase())
        )
      )
    );

    if (matchingImages.length > 0) {
      // Select the most relevant image (first match for now)
      onImageSelect(matchingImages[0].image_url!);
      toast.success('Auto-selected matching image from gallery');
    } else if (allSearchTerms.length > 0) {
      // No matching image found, suggest AI generation
      toast.info('No matching images found in gallery. Consider generating one with AI.');
      setGeneratePrompt(`Create an image related to: ${allSearchTerms.join(', ')}`);
    }
  };

  const handleGenerateImage = async () => {
    if (!generatePrompt.trim()) {
      toast.error('Please enter a prompt for image generation');
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-image', {
        body: {
          prompt: generatePrompt
        }
      });

      if (error) throw error;

      onImageSelect(data.imageUrl);
      toast.success('Image generated successfully!');
      
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      // Convert file to data URL for preview
      const reader = new FileReader();
      reader.onloadend = () => {
        onImageSelect(reader.result as string);
        toast.success('Image uploaded successfully');
      };
      reader.readAsDataURL(uploadFile);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload image');
    }
  };

  const availableImages = imageVariables?.filter(v => v.image_url) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Featured Image
        </CardTitle>
        <CardDescription>
          Choose how to select your blog post's featured image
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Auto/Manual Toggle */}
        <div className="flex items-center space-x-2">
          <Switch
            id="auto-mode"
            checked={isAutoMode}
            onCheckedChange={setIsAutoMode}
          />
          <Label htmlFor="auto-mode">
            {isAutoMode ? 'Auto-select from gallery' : 'Manual selection'}
          </Label>
        </div>

        {/* Current Image Preview */}
        {selectedImage && (
          <div className="space-y-2">
            <Label>Selected Image:</Label>
            <div className="relative aspect-video w-full max-w-md overflow-hidden rounded-md border">
              <img
                src={selectedImage}
                alt="Selected featured image"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Auto Mode Description */}
        {isAutoMode && (
          <div className="text-sm text-muted-foreground p-3 bg-blue-50 rounded-md">
            <p>Auto mode will:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Find unused images from gallery matching your tags/categories</li>
              <li>Suggest AI generation if no suitable images are found</li>
            </ul>
            {blogTags.length === 0 && blogCategories.length === 0 && (
              <p className="text-amber-600 mt-2">
                Add tags or categories to improve auto-selection
              </p>
            )}
          </div>
        )}

        {/* Manual Mode Options */}
        {!isAutoMode && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Manual Selection Mode:</Label>
              <Select value={manualMode} onValueChange={(value: 'select' | 'upload' | 'generate') => setManualMode(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose selection method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="select">Select from Gallery</SelectItem>
                  <SelectItem value="upload">Upload Image</SelectItem>
                  <SelectItem value="generate">Generate with AI</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Select from Gallery */}
            {manualMode === 'select' && (
              <div className="space-y-3">
                <Label>Choose from Gallery:</Label>
                {availableImages.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                    {availableImages.map((variable) => (
                      <div
                        key={variable.id}
                        className={`relative aspect-square cursor-pointer rounded-md overflow-hidden border-2 transition-colors ${
                          selectedImage === variable.image_url 
                            ? 'border-primary' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => onImageSelect(variable.image_url!)}
                      >
                        <img
                          src={variable.image_url!}
                          alt={variable.alt_tag}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all" />
                        <div className="absolute bottom-1 left-1 right-1">
                          <div className="flex flex-wrap gap-1">
                            {variable.keywords.slice(0, 2).map((keyword, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {keyword}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No images available in gallery
                  </p>
                )}
              </div>
            )}

            {/* Upload Image */}
            {manualMode === 'upload' && (
              <div className="space-y-3">
                <Label htmlFor="image-upload">Upload Image:</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleFileUpload} 
                    disabled={!uploadFile}
                    variant="outline"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </Button>
                </div>
              </div>
            )}

            {/* Generate with AI */}
            {manualMode === 'generate' && (
              <div className="space-y-3">
                <Label htmlFor="generate-prompt">AI Image Prompt:</Label>
                <Textarea
                  id="generate-prompt"
                  value={generatePrompt}
                  onChange={(e) => setGeneratePrompt(e.target.value)}
                  placeholder="Describe the image you want to generate..."
                  rows={3}
                />
                <Button 
                  onClick={handleGenerateImage} 
                  disabled={isGenerating || !generatePrompt.trim()}
                  className="w-full"
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  {isGenerating ? 'Generating...' : 'Generate Image'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Force Auto-selection Button */}
        {isAutoMode && (blogTags.length > 0 || blogCategories.length > 0) && (
          <Button 
            onClick={findMatchingImage} 
            variant="outline" 
            className="w-full"
          >
            <Image className="h-4 w-4 mr-2" />
            Find Matching Image
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ImageSelector;
